/**
 * Timeline commands: pure functions over the scene's timeline (phases, tracks, keyframes, steps, custom actions,
 * update transitions), plus frame snapping and timecode for display. The timeline UI and the inspector's
 * Animation section call these; each is unit-tested without a browser.
 *
 * Phases map to OGraf actions: in (first playAction → hold at step 0), step n transition (playAction to step n),
 * step n hold loop, update (updateAction), per-field update sub-animations, out (stopAction or play past the last
 * step) and custom actions (customAction).
 */
import {
  ANIMATABLE_PROPERTIES,
  AnimatablePropertySchema,
  IdSchema,
  KeyframeSchema,
  STAGGER_PROPERTIES,
  StaggerSchema,
  formatTimecode,
  frameToMs,
  msToFrame,
  snapToFrame,
  type AnimatableProperty,
  type CustomAction,
  type Easing,
  type Element,
  type FrameRate,
  type Keyframe,
  type KeyframeValue,
  type Phase,
  type Scene,
  type Step,
  type Timeline,
  type Track,
  type UpdateTransition,
} from '@prism-graphics/scene-schema';
import { sampleTrack } from '@prism-graphics/runtime';
import { z } from 'zod';
import { uniqueId } from './ids.ts';
import { findElement, updateElement, walk } from './tree.ts';

export type PhaseRef =
  | { kind: 'in' }
  | { kind: 'step'; index: number }
  | { kind: 'loop'; index: number }
  | { kind: 'out' }
  | { kind: 'update' }
  | { kind: 'field'; field: string }
  | { kind: 'custom'; index: number };

/** A keyframe in a phase: track index and keyframe index. */
export interface KeyRef {
  track: number;
  key: number;
}

export const samePhase = (a: PhaseRef, b: PhaseRef): boolean => JSON.stringify(a) === JSON.stringify(b);
export const phaseKey = (r: PhaseRef): string => JSON.stringify(r);

// ---------------------------------------------------------------------------------------------------------------
// Frame time

/** Snap a time (ms) to the nearest frame start at the canvas rate. */
export function snap(ms: number, rate: FrameRate): number {
  return Math.max(0, snapToFrame(ms, rate));
}

export function frameOf(ms: number, rate: FrameRate): number {
  return msToFrame(ms + 1e-6, rate);
}

/** "25 f · 00:00:01:00 · 1000 ms" style display parts. */
export function timeLabel(ms: number, rate: FrameRate): { frames: string; timecode: string; ms: string } {
  const f = frameOf(ms, rate);
  return { frames: `${f} f`, timecode: formatTimecode(f, rate), ms: `${Math.round(ms)} ms` };
}

/** One frame later or earlier (or `n` frames), from the frame that contains `ms`. */
export function stepFrames(ms: number, n: number, rate: FrameRate): number {
  return Math.max(0, frameToMs(frameOf(ms, rate) + n, rate));
}

// ---------------------------------------------------------------------------------------------------------------
// Phases

/** Every phase the timeline has (or can have), in the order the strip shows them. */
export function listPhases(scene: Scene): PhaseRef[] {
  const t = scene.timeline;
  const list: PhaseRef[] = [{ kind: 'in' }];
  t.steps.forEach((s, i) => {
    if (i > 0 || t.steps.length > 1) list.push({ kind: 'step', index: i });
    if (s.loop) list.push({ kind: 'loop', index: i });
  });
  list.push({ kind: 'out' }, { kind: 'update' });
  for (const [field, tr] of Object.entries(t.update.fields)) if (tr.mode === 'phase') list.push({ kind: 'field', field });
  t.customActions.forEach((_, i) => list.push({ kind: 'custom', index: i }));
  return list;
}

const EMPTY: Phase = { duration: 0, tracks: [] };

export function getPhase(scene: Scene, ref: PhaseRef): Phase | undefined {
  const t = scene.timeline;
  switch (ref.kind) {
    case 'in':
      return t.in;
    case 'out':
      return t.out;
    case 'step':
      return t.steps[ref.index] ? (t.steps[ref.index]!.enter ?? EMPTY) : undefined;
    case 'loop':
      return t.steps[ref.index]?.loop;
    case 'update':
      return { duration: t.update.duration, tracks: t.update.tracks };
    case 'field': {
      const tr = t.update.fields[ref.field];
      return tr?.mode === 'phase' ? tr.phase : undefined;
    }
    case 'custom':
      return t.customActions[ref.index]?.phase;
  }
}

function withTimeline(scene: Scene, patch: Partial<Timeline>): Scene {
  return { ...scene, timeline: { ...scene.timeline, ...patch } };
}

export function setPhase(scene: Scene, ref: PhaseRef, phase: Phase): Scene {
  const t = scene.timeline;
  switch (ref.kind) {
    case 'in':
      return withTimeline(scene, { in: phase });
    case 'out':
      return withTimeline(scene, { out: phase });
    case 'step':
    case 'loop': {
      if (!t.steps[ref.index]) return scene;
      const key = ref.kind === 'step' ? 'enter' : 'loop';
      return withTimeline(scene, { steps: t.steps.map((s, i) => (i === ref.index ? { ...s, [key]: phase } : s)) });
    }
    case 'update':
      return withTimeline(scene, { update: { ...t.update, duration: phase.duration, tracks: phase.tracks } });
    case 'field':
      return withTimeline(scene, { update: { ...t.update, fields: { ...t.update.fields, [ref.field]: { mode: 'phase', phase } } } });
    case 'custom':
      if (!t.customActions[ref.index]) return scene;
      return withTimeline(scene, { customActions: t.customActions.map((a, i) => (i === ref.index ? { ...a, phase } : a)) });
  }
}

export function updatePhase(scene: Scene, ref: PhaseRef, fn: (p: Phase) => Phase): Scene {
  const p = getPhase(scene, ref);
  if (!p) return scene;
  const next = fn(p);
  return next === p ? scene : setPhase(scene, ref, next);
}

/** Set a phase's duration (snapped to frames); keyframes after the new end move to it. */
export function setPhaseDuration(scene: Scene, ref: PhaseRef, ms: number): Scene {
  const duration = snap(Math.max(0, ms), scene.canvas.frameRate);
  return updatePhase(scene, ref, (p) => ({
    duration,
    tracks: p.tracks.map((tr) => ({ ...tr, keyframes: sortKeys(tr.keyframes.map((k) => (k.time > duration ? { ...k, time: duration } : k))) })),
  }));
}

// ---------------------------------------------------------------------------------------------------------------
// Steps, sting mode, custom actions, update transitions

export function addStep(scene: Scene): Scene {
  const t = scene.timeline;
  const id = uniqueId('step', new Set(t.steps.map((s) => s.id)));
  const step: Step = { id, name: `Step ${t.steps.length + 1}`, enter: { duration: snap(500, scene.canvas.frameRate), tracks: [] } };
  const { autoOutAfter: _a, ...rest } = t;
  return { ...scene, timeline: { ...rest, steps: [...t.steps, step] } };
}

export function removeStep(scene: Scene, index: number): Scene {
  const t = scene.timeline;
  if (!t.steps[index] || t.steps.length <= 1) return scene;
  return withTimeline(scene, { steps: t.steps.filter((_, i) => i !== index) });
}

export function updateStep(scene: Scene, index: number, patch: Partial<Step>): Scene {
  const t = scene.timeline;
  if (!t.steps[index]) return scene;
  return withTimeline(scene, {
    steps: t.steps.map((s, i) => {
      if (i !== index) return s;
      const next = { ...s, ...patch };
      for (const k of ['enter', 'loop'] as const) if (k in patch && patch[k] === undefined) delete next[k];
      return next;
    }),
  });
}

/** stepCount 0 ("sting"): no steps, the out runs `autoOutAfter` ms after the in. Off: back to one step. */
export function setSting(scene: Scene, on: boolean, autoOutAfter = 2000): Scene {
  const t = scene.timeline;
  if (on) return withTimeline(scene, { steps: [], autoOutAfter: snap(autoOutAfter, scene.canvas.frameRate) });
  if (t.steps.length > 0) return scene;
  const { autoOutAfter: _a, ...rest } = t;
  return { ...scene, timeline: { ...rest, steps: [{ id: 'step0', name: 'Step 1' }] } };
}

export function setAutoOutAfter(scene: Scene, ms: number): Scene {
  if (scene.timeline.steps.length > 0) return scene;
  return withTimeline(scene, { autoOutAfter: snap(Math.max(0, ms), scene.canvas.frameRate) });
}

export function addCustomAction(scene: Scene, name = 'Custom action'): { scene: Scene; index: number } {
  const t = scene.timeline;
  const taken = new Set(t.customActions.map((a) => a.id));
  const slug = name.normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
  let id = /^[a-z]/.test(slug) ? slug.slice(0, 40) : 'action';
  if (taken.has(id)) id = uniqueId(`${id}-`, taken);
  const action: CustomAction = { id, name, schema: null, phase: { duration: snap(500, scene.canvas.frameRate), tracks: [] } };
  return { scene: withTimeline(scene, { customActions: [...t.customActions, action] }), index: t.customActions.length };
}

export function updateCustomAction(scene: Scene, index: number, patch: Partial<CustomAction>): Scene {
  const t = scene.timeline;
  if (!t.customActions[index]) return scene;
  return withTimeline(scene, { customActions: t.customActions.map((a, i) => (i === index ? { ...a, ...patch } : a)) });
}

export function removeCustomAction(scene: Scene, index: number): Scene {
  const t = scene.timeline;
  return withTimeline(scene, { customActions: t.customActions.filter((_, i) => i !== index) });
}

/** Per-field update transition. `phase` re-runs a sub-animation (seeded from the In tracks of the bound elements). */
export function setFieldTransition(scene: Scene, fieldId: string, mode: UpdateTransition['mode']): Scene {
  const t = scene.timeline;
  const fields = { ...t.update.fields };
  if (mode === 'cut') delete fields[fieldId];
  else if (mode === 'crossfade') fields[fieldId] = { mode: 'crossfade' };
  else {
    const current = fields[fieldId];
    if (current?.mode === 'phase') return scene;
    const field = scene.fields.find((f) => f.id === fieldId);
    const bound = new Set(field?.bindings.map((b) => b.element) ?? []);
    const tracks = t.in.tracks.filter((tr) => bound.has(tr.element));
    fields[fieldId] = { mode: 'phase', phase: { duration: tracks.length ? t.in.duration : t.update.duration, tracks } };
  }
  return withTimeline(scene, { update: { ...t.update, fields } });
}

export function setUpdateTiming(scene: Scene, patch: { duration?: number; easing?: Easing }): Scene {
  const t = scene.timeline;
  const duration = patch.duration === undefined ? t.update.duration : snap(patch.duration, scene.canvas.frameRate);
  return withTimeline(scene, { update: { ...t.update, duration, ...(patch.easing ? { easing: patch.easing } : {}) } });
}

// ---------------------------------------------------------------------------------------------------------------
// Tracks and keyframes

export function sortKeys(keys: Keyframe[]): Keyframe[] {
  return [...keys].sort((a, b) => a.time - b.time);
}

export function findTrack(phase: Phase, element: string, property: AnimatableProperty, stagger = false): number {
  return phase.tracks.findIndex((t) => t.element === element && t.property === property && Boolean(t.stagger) === stagger);
}

/** Properties an element type can animate, in display order. */
export function animatableFor(el: Element): AnimatableProperty[] {
  return ANIMATABLE_PROPERTIES.filter((p) => {
    if (p === 'letterSpacing') return el.type === 'text';
    // Auto sizes are measured by the runtime (and animate on update by themselves).
    if (p === 'width' || p === 'height') return typeof el.size[p] === 'number';
    if (p === 'color') return el.type === 'text' || el.type === 'rect' || el.type === 'ellipse' || (el.type === 'group' && el.style.fill?.type !== 'linear-gradient');
    return true;
  });
}

/** The designed value of a property (what `{ rel: 0 }` means) as far as the model knows it. */
export function designValue(el: Element, p: AnimatableProperty): number | string {
  const t = el.transform;
  switch (p) {
    case 'x':
    case 'y':
    case 'rotation':
    case 'scaleX':
    case 'scaleY':
    case 'opacity':
      return t[p];
    case 'width':
    case 'height':
      return typeof el.size[p] === 'number' ? (el.size[p] as number) : 0;
    case 'blur':
      return el.type !== 'text' && 'style' in el ? ((el.style as { blur?: number }).blur ?? 0) : 0;
    case 'letterSpacing':
      return el.type === 'text' ? el.style.letterSpacing : 0;
    case 'color':
      if (el.type === 'text') return el.style.color;
      if ((el.type === 'rect' || el.type === 'ellipse' || el.type === 'group') && el.style.fill?.type === 'solid') return el.style.fill.color;
      return '#FFFFFF';
    default:
      return 0;
  }
}

/** The value a property has at local time `time` of a phase (its track sampled, else the designed value). */
export function valueAt(scene: Scene, phase: Phase | undefined, element: string, property: AnimatableProperty, time: number): number | string {
  const el = findElement(scene, element);
  const base = el ? designValue(el, property) : 0;
  const i = phase ? findTrack(phase, element, property) : -1;
  if (!phase || i < 0) return base;
  return sampleTrack(phase.tracks[i]!, time, base);
}

/** A colour as #RRGGBB or #RRGGBBAA (the engine's interpolated rgb()/rgba() output converted back). */
export function toHexColor(v: string): string {
  const m = /^rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)$/.exec(v);
  if (!m) return /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v) ? v.toUpperCase() : '#FFFFFF';
  const h = (n: number) => Math.min(255, Math.max(0, Math.round(n))).toString(16).padStart(2, '0').toUpperCase();
  const a = m[4] === undefined ? 'FF' : h(Number(m[4]) * 255);
  return `#${h(Number(m[1]))}${h(Number(m[2]))}${h(Number(m[3]))}${a === 'FF' ? '' : a}`;
}

/** Whether a track stores relative values (most of its keyframes are `{ rel }`). */
function relative(track: Track | undefined, property: AnimatableProperty): boolean {
  if (property === 'color') return false;
  if (!track) return true;
  const rel = track.keyframes.filter((k) => typeof k.value === 'object').length;
  return rel * 2 >= track.keyframes.length;
}

/**
 * Set a keyframe at `time` (snapped) to `value` (absolute), creating the track when needed. Relative tracks store
 * `{ rel: value − designed }`, so the animation keeps working when the design or bound data changes.
 */
export function setKeyAt(scene: Scene, ref: PhaseRef, element: string, property: AnimatableProperty, time: number, value: number | string, easing?: Easing): Scene {
  const el = findElement(scene, element);
  if (!el) return scene;
  const t = snap(time, scene.canvas.frameRate);
  return updatePhase(scene, ref, (p) => {
    const i = findTrack(p, element, property);
    const track = i >= 0 ? p.tracks[i] : undefined;
    const base = designValue(el, property);
    const kv: KeyframeValue =
      typeof value === 'number' && relative(track, property) && typeof base === 'number' ? { rel: round(value - base) } : typeof value === 'number' ? round(value) : toHexColor(value);
    const tracks = [...p.tracks];
    if (!track) {
      tracks.push({ element, property, keyframes: [{ time: t, value: kv, easing: easing ?? 'easeInOutCubic' }] });
    } else {
      const existing = track.keyframes.findIndex((k) => Math.abs(k.time - t) < 0.5);
      const keyframes =
        existing >= 0
          ? track.keyframes.map((k, j) => (j === existing ? { ...k, value: kv, ...(easing ? { easing } : {}) } : k))
          : sortKeys([...track.keyframes, { time: t, value: kv, easing: easing ?? track.keyframes[0]!.easing }]);
      tracks[i] = { ...track, keyframes };
    }
    return { ...p, duration: Math.max(p.duration, t), tracks };
  });
}

/** Remove the keyframe at `time` (if any) of a property's track; an emptied track is removed. */
export function removeKeyAt(scene: Scene, ref: PhaseRef, element: string, property: AnimatableProperty, time: number): Scene {
  return updatePhase(scene, ref, (p) => {
    const i = findTrack(p, element, property);
    if (i < 0) return p;
    const k = p.tracks[i]!.keyframes.findIndex((kf) => Math.abs(kf.time - time) < 0.5);
    if (k < 0) return p;
    return deleteKeysIn(p, [{ track: i, key: k }]);
  });
}

export function deleteKeysIn(p: Phase, keys: readonly KeyRef[]): Phase {
  const drop = new Set(keys.map((k) => `${k.track}:${k.key}`));
  const tracks = p.tracks
    .map((tr, ti) => ({ ...tr, keyframes: tr.keyframes.filter((_, ki) => !drop.has(`${ti}:${ki}`)) }))
    .filter((tr) => tr.keyframes.length > 0);
  return { ...p, tracks };
}

export function removeTrack(p: Phase, track: number): Phase {
  return { ...p, tracks: p.tracks.filter((_, i) => i !== track) };
}

/**
 * Move keyframes by `dt` ms (snapped to frames, clamped to the phase). Returns the phase and where the moved keys
 * ended up (tracks stay sorted by time).
 */
export function moveKeysIn(p: Phase, keys: readonly KeyRef[], dt: number, rate: FrameRate): { phase: Phase; keys: KeyRef[] } {
  const sel = new Set(keys.map((k) => `${k.track}:${k.key}`));
  let lo = Infinity;
  let hi = -Infinity;
  for (const k of keys) {
    const kf = p.tracks[k.track]?.keyframes[k.key];
    if (!kf) continue;
    lo = Math.min(lo, kf.time);
    hi = Math.max(hi, kf.time);
  }
  if (!Number.isFinite(lo)) return { phase: p, keys: [...keys] };
  const d = Math.min(Math.max(dt, -lo), p.duration - hi);
  const out: KeyRef[] = [];
  const tracks = p.tracks.map((tr, ti) => {
    const moved = new Set<Keyframe>();
    const kfs = tr.keyframes.map((kf, ki) => {
      if (!sel.has(`${ti}:${ki}`)) return kf;
      const n = { ...kf, time: Math.min(p.duration, snap(kf.time + d, rate)) };
      moved.add(n);
      return n;
    });
    // A moved key replaces an unmoved key at the same frame.
    const kept = kfs.filter((kf) => moved.has(kf) || ![...moved].some((m) => Math.abs(m.time - kf.time) < 0.5));
    const sorted = sortKeys(kept);
    sorted.forEach((kf, ki) => moved.has(kf) && out.push({ track: ti, key: ki }));
    return { ...tr, keyframes: sorted };
  });
  return { phase: { ...p, tracks }, keys: out };
}

export function setEasingIn(p: Phase, keys: readonly KeyRef[], easing: Easing): Phase {
  const sel = new Set(keys.map((k) => `${k.track}:${k.key}`));
  return { ...p, tracks: p.tracks.map((tr, ti) => ({ ...tr, keyframes: tr.keyframes.map((kf, ki) => (sel.has(`${ti}:${ki}`) ? { ...kf, easing } : kf)) })) };
}

/** Keyframes on the clipboard: per track (element, property, stagger) the keys relative to the earliest one. */
export interface KeyClip {
  kind: 'prism-graphics/keyframes';
  tracks: Array<{ element: string; property: AnimatableProperty; stagger?: Track['stagger']; keyframes: Keyframe[] }>;
}

export function copyKeysIn(p: Phase, keys: readonly KeyRef[]): KeyClip | undefined {
  const times = keys.map((k) => p.tracks[k.track]?.keyframes[k.key]?.time).filter((x): x is number => x !== undefined);
  if (times.length === 0) return undefined;
  const t0 = Math.min(...times);
  const byTrack = new Map<number, Keyframe[]>();
  for (const k of keys) {
    const kf = p.tracks[k.track]?.keyframes[k.key];
    if (!kf) continue;
    byTrack.set(k.track, [...(byTrack.get(k.track) ?? []), { ...kf, time: kf.time - t0 }]);
  }
  return {
    kind: 'prism-graphics/keyframes',
    tracks: [...byTrack].map(([ti, kfs]) => {
      const tr = p.tracks[ti]!;
      return { element: tr.element, property: tr.property, ...(tr.stagger ? { stagger: tr.stagger } : {}), keyframes: sortKeys(kfs) };
    }),
  };
}

/**
 * Paste keyframes at `at` ms. With `onto` (one element id) every clip track goes to that element; otherwise to
 * the elements it was copied from (ones that no longer exist are skipped). The phase grows to fit.
 */
export function pasteKeysIn(scene: Scene, p: Phase, clip: KeyClip, at: number, onto?: string): Phase {
  const rate = scene.canvas.frameRate;
  let duration = p.duration;
  const tracks = [...p.tracks];
  for (const ct of clip.tracks) {
    const element = onto ?? ct.element;
    const el = findElement(scene, element);
    if (!el || (ct.stagger && el.type !== 'text') || !animatableFor(el).includes(ct.property)) continue;
    const kfs = ct.keyframes.map((k) => ({ ...k, time: snap(at + k.time, rate) }));
    duration = Math.max(duration, ...kfs.map((k) => k.time));
    const i = tracks.findIndex((t) => t.element === element && t.property === ct.property && JSON.stringify(t.stagger) === JSON.stringify(ct.stagger));
    if (i < 0) tracks.push({ element, property: ct.property, ...(ct.stagger ? { stagger: ct.stagger } : {}), keyframes: kfs });
    else {
      const replaced = tracks[i]!.keyframes.filter((k) => !kfs.some((n) => Math.abs(n.time - k.time) < 0.5));
      tracks[i] = { ...tracks[i]!, keyframes: sortKeys([...replaced, ...kfs]) };
    }
  }
  return { duration, tracks };
}

/** Keyframes from the clipboard are untrusted: validated like the scene's own tracks (values match the property). */
const KeyClipSchema = z.object({
  kind: z.literal('prism-graphics/keyframes'),
  tracks: z.array(
    z
      .object({ element: IdSchema, property: AnimatablePropertySchema, stagger: StaggerSchema.optional(), keyframes: z.array(KeyframeSchema).min(1) })
      .refine((tr) => tr.keyframes.every((k) => (tr.property === 'color' ? typeof k.value !== 'number' : typeof k.value !== 'string')))
      .refine((tr) => !tr.stagger || (STAGGER_PROPERTIES as readonly string[]).includes(tr.property)),
  ),
});

export function parseKeyClip(text: string | undefined): KeyClip | undefined {
  if (!text) return undefined;
  try {
    const r = KeyClipSchema.safeParse(JSON.parse(text));
    if (!r.success) return undefined;
    const tracks = r.data.tracks.map(({ stagger, ...tr }) => ({ ...tr, ...(stagger ? { stagger } : {}), keyframes: sortKeys(tr.keyframes as Keyframe[]) }));
    return { kind: r.data.kind, tracks } as KeyClip;
  } catch {
    return undefined;
  }
}

/** Keys whose time falls in [t0, t1] on the given tracks (marquee selection). */
export function keysInRange(p: Phase, tracks: readonly number[], t0: number, t1: number): KeyRef[] {
  const lo = Math.min(t0, t1);
  const hi = Math.max(t0, t1);
  const out: KeyRef[] = [];
  for (const ti of tracks) p.tracks[ti]?.keyframes.forEach((k, ki) => k.time >= lo - 0.5 && k.time <= hi + 0.5 && out.push({ track: ti, key: ki }));
  return out;
}

/** Remove every track of elements that no longer exist is done by dropDanglingReferences; this drops one element's tracks in one phase. */
export function clearElementTracks(p: Phase, element: string): Phase {
  return { ...p, tracks: p.tracks.filter((t) => t.element !== element) };
}

const round = (n: number) => Math.round(n * 1000) / 1000;

const AUTO_KEYED = ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'width', 'height'] as const;

/**
 * Auto-key: turn design changes of position, rotation, scale and size (a canvas drag or a nudge from `prev` to
 * `next`) into keyframes at the playhead of a phase. The designed value stays; the keyframe gets the value shown at
 * the playhead plus the change, so dragging moves the element from where it is at that moment.
 */
export function autoKey(prev: Scene, next: Scene, ref: PhaseRef, time: number): Scene {
  if (prev === next) return next;
  const phase = getPhase(prev, ref);
  if (!phase) return next;
  let out = next;
  walk(prev.elements, (before) => {
    const after = findElement(next, before.id);
    if (!after) return;
    for (const p of AUTO_KEYED) {
      const v0 = designValue(before, p);
      const v1 = designValue(after, p);
      if (typeof v0 !== 'number' || typeof v1 !== 'number' || Math.abs(v1 - v0) < 1e-9) continue;
      if ((p === 'width' || p === 'height') && typeof before.size[p] !== 'number') continue;
      const shown = valueAt(prev, phase, before.id, p, time);
      out = updateElement(out, before.id, (el) =>
        p === 'width' || p === 'height' ? ({ ...el, size: { ...el.size, [p]: v0 } } as Element) : ({ ...el, transform: { ...el.transform, [p]: v0 } } as Element),
      );
      out = setKeyAt(out, ref, before.id, p, time, (typeof shown === 'number' ? shown : v0) + (v1 - v0));
    }
  });
  return out;
}
